import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
    XMarkIcon,
    CalendarIcon,
    TagIcon,
    CurrencyDollarIcon,
    UserGroupIcon,
    PhotoIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';

function CampaignCreateModal({ isOpen, onClose, onSuccess, editingCampaign = null }) {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        campaign_type: 'flash_sale',
        status: 'draft',
        start_date: '',
        end_date: '',
        priority: 1,
        discount_type: 'percentage',
        discount_value: 0,
        max_discount_amount: '',
        usage_limit_total: '',
        usage_limit_per_user: 1,
        min_cart_amount: 0,
        max_cart_amount: '',
        coupon_code: '',
        auto_apply: false,
        banner_image_url: '',
        badge_text: '',
        badge_color: '#ff4444',
        show_countdown: false,
        highlight_products: false,
        selectedProducts: [],
        selectedCategories: [],
        // Paket kampanyası alanları
        package_buy_count: '2',
        package_pay_count: '1',
        package_type: 'same_product',
        // Özel gün kampanyası alanları
        special_day_type: 'mothers_day',
        special_day_date: '',
        auto_activate: true,
        target_audience: 'all',
        campaign_start_time: '00:00',
        campaign_end_time: '23:59'
    });

    const campaignTypes = [
        { value: 'flash_sale', label: 'Flash Sale', description: 'Sınırlı süre anlık indirimler' },
        { value: 'category_discount', label: 'Kategori İndirimi', description: 'Belirli kategorilerde indirim' },
        { value: 'cart_discount', label: 'Sepet İndirimi', description: 'Sepet tutarına göre indirim' },
        { value: 'coupon', label: 'Kupon Kodu', description: 'Kod ile kullanılan indirimler' },
        { value: 'bundle', label: 'Paket Kampanyası', description: '2 al 1 öde, 3 al 2 öde gibi paket fırsatları' },
        { value: 'special_day', label: 'Özel Gün', description: 'Anneler günü, yılbaşı vb.' }
    ];

    const discountTypes = [
        { value: 'percentage', label: 'Yüzde (%)', description: 'Yüzde olarak indirim' },
        { value: 'fixed_amount', label: 'Sabit Tutar (TL)', description: 'Sabit tutar indirim' },
        { value: 'buy_x_get_y', label: 'X Al Y Öde', description: '2 al 1 öde tarzı' }
    ];

    useEffect(() => {
        if (isOpen) {
            fetchProducts();
            fetchCategories();
            
            if (editingCampaign) {
                // Tarih formatını düzelt - datetime-local için YYYY-MM-DDTHH:MM formatı gerekli
                const formatDateForInput = (dateString) => {
                    if (!dateString) return '';
                    const date = new Date(dateString);
                    return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM formatı
                };

                setFormData({
                    ...editingCampaign,
                    start_date: formatDateForInput(editingCampaign.start_date),
                    end_date: formatDateForInput(editingCampaign.end_date),
                    selectedProducts: editingCampaign.campaign_products?.map(cp => cp.product_id) || [],
                    selectedCategories: editingCampaign.campaign_categories?.map(cc => cc.category_id) || []
                });
            }
        }
    }, [isOpen, editingCampaign]);

    const fetchProducts = async () => {
        try {
            const response = await api('/api/products?limit=100');
            setProducts(response.products || []);
        } catch (error) {
            console.error('Ürünler yüklenirken hata:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await api('/api/categories');
            console.log('Categories API Response:', response);
            // API yanıtının formatını kontrol et
            const categoriesData = response.data || response.categories || response || [];
            setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        } catch (error) {
            console.error('Kategoriler yüklenirken hata:', error);
            setCategories([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleProductSelection = (productId) => {
        setFormData(prev => ({
            ...prev,
            selectedProducts: prev.selectedProducts.includes(productId)
                ? prev.selectedProducts.filter(id => id !== productId)
                : [...prev.selectedProducts, productId]
        }));
    };

    const handleCategorySelection = (categoryId) => {
        setFormData(prev => ({
            ...prev,
            selectedCategories: prev.selectedCategories.includes(categoryId)
                ? prev.selectedCategories.filter(id => id !== categoryId)
                : [...prev.selectedCategories, categoryId]
        }));
    };

    const generateCouponCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setFormData(prev => ({ ...prev, coupon_code: result }));
    };

    const handleBannerUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Dosya boyutu kontrolü (5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
            return;
        }

        // Dosya türü kontrolü
        if (!file.type.startsWith('image/')) {
            toast.error('Sadece resim dosyaları yüklenebilir');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('banner', file);

            const response = await api('/api/banner-upload/upload', {
                method: 'POST',
                body: formData,
                // Content-Type header'ını kaldır, browser otomatik ayarlayacak
                headers: {}
            });

            if (response.success) {
                // HTTPS için URL'i düzelt
                let bannerUrl = response.data.fullUrl;
                if (bannerUrl.startsWith('http://')) {
                    bannerUrl = bannerUrl.replace('http://', 'https://');
                }
                
                setFormData(prev => ({
                    ...prev,
                    banner_image_url: bannerUrl
                }));
                toast.success('Banner resmi başarıyla yüklendi');
            } else {
                throw new Error(response.error || 'Yükleme başarısız');
            }
        } catch (error) {
            console.error('Banner yükleme hatası:', error);
            toast.error('Banner yüklenirken hata oluştu: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            toast.error('Kampanya adı gerekli');
            return false;
        }
        
        if (!formData.start_date || !formData.end_date) {
            toast.error('Başlangıç ve bitiş tarihleri gerekli');
            return false;
        }
        
        if (new Date(formData.start_date) >= new Date(formData.end_date)) {
            toast.error('Bitiş tarihi başlangıç tarihinden sonra olmalıdır');
            return false;
        }
        
        if (formData.discount_value <= 0) {
            toast.error('İndirim değeri 0\'dan büyük olmalıdır');
            return false;
        }
        
        if (formData.campaign_type === 'coupon' && !formData.coupon_code.trim()) {
            toast.error('Kupon kampanyaları için kupon kodu gerekli');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        try {
            const submitData = {
                ...formData,
                // Numeric alanları temizle - boş string'leri null yap
                discount_value: parseFloat(formData.discount_value) || 0,
                max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
                usage_limit_total: formData.usage_limit_total ? parseInt(formData.usage_limit_total) : null,
                usage_limit_per_user: parseInt(formData.usage_limit_per_user) || 1,
                min_cart_amount: parseFloat(formData.min_cart_amount) || 0,
                max_cart_amount: formData.max_cart_amount ? parseFloat(formData.max_cart_amount) : null,
                priority: parseInt(formData.priority) || 1,
                products: formData.selectedProducts.map(productId => ({
                    product_id: productId,
                    custom_discount_value: null,
                    stock_limit: null
                })),
                categories: formData.selectedCategories.map(categoryId => ({
                    category_id: categoryId,
                    include_subcategories: true,
                    custom_discount_value: null
                }))
            };

            // Gereksiz alanları temizle
            delete submitData.selectedProducts;
            delete submitData.selectedCategories;
            delete submitData.campaign_products;
            delete submitData.campaign_categories;
            delete submitData.id;
            delete submitData.created_at;
            delete submitData.updated_at;
            delete submitData.total_usage_count;
            delete submitData.total_discount_given;
            delete submitData.total_revenue;
            delete submitData.conversion_rate;

            const url = editingCampaign 
                ? `/api/campaigns/${editingCampaign.id}`
                : '/api/campaigns';
            
            const method = editingCampaign ? 'PUT' : 'POST';

            await api(url, {
                method,
                body: JSON.stringify(submitData)
            });

            toast.success(editingCampaign ? 'Kampanya güncellendi' : 'Kampanya oluşturuldu');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Kampanya kaydedilirken hata:', error);
            toast.error('Kampanya kaydedilemedi: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                        {editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya Oluştur'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Temel Bilgiler */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Kampanya Adı *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Kampanya Türü *
                            </label>
                            <select
                                name="campaign_type"
                                value={formData.campaign_type}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                            >
                                {campaignTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label} - {type.description}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Açıklama
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                        />
                    </div>

                    {/* Tarih Aralığı */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <CalendarIcon className="w-4 h-4 inline mr-1" />
                                Başlangıç Tarihi *
                            </label>
                            <input
                                type="datetime-local"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <CalendarIcon className="w-4 h-4 inline mr-1" />
                                Bitiş Tarihi *
                            </label>
                            <input
                                type="datetime-local"
                                name="end_date"
                                value={formData.end_date}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                required
                            />
                        </div>
                    </div>

                    {/* İndirim Ayarları */}
                    <div className="border-t pt-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">
                            <CurrencyDollarIcon className="w-5 h-5 inline mr-1" />
                            İndirim Ayarları
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    İndirim Türü *
                                </label>
                                <select
                                    name="discount_type"
                                    value={formData.discount_type}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                >
                                    {discountTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    İndirim Değeri *
                                </label>
                                <input
                                    type="number"
                                    name="discount_value"
                                    value={formData.discount_value}
                                    onChange={handleInputChange}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Maksimum İndirim (TL)
                                </label>
                                <input
                                    type="number"
                                    name="max_discount_amount"
                                    value={formData.max_discount_amount}
                                    onChange={handleInputChange}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Banner ve Görsel Ayarları */}
                    <div className="border-t pt-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">
                            <PhotoIcon className="w-5 h-5 inline mr-1" />
                            Banner ve Görsel Ayarları
                        </h4>
                        
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Banner Resmi
                                </label>
                                <div className="space-y-2">
                                    {/* Dosya Yükleme */}
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleBannerUpload}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-green file:text-white hover:file:bg-brand-green-light"
                                        />
                                    </div>
                                    
                                    {/* URL Girişi */}
                                    <div className="text-center text-gray-500 text-sm">veya</div>
                                    <input
                                        type="url"
                                        name="banner_image_url"
                                        value={formData.banner_image_url}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                        placeholder="https://example.com/banner.jpg"
                                    />
                                    
                                    {/* Önizleme */}
                                    {formData.banner_image_url && (
                                        <div className="mt-2">
                                            <img
                                                src={formData.banner_image_url}
                                                alt="Banner önizleme"
                                                className="w-full h-32 object-cover rounded-md border"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Badge Metni
                                </label>
                                <input
                                    type="text"
                                    name="badge_text"
                                    value={formData.badge_text}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                    placeholder="FLASH SALE"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Badge Rengi
                                </label>
                                <input
                                    type="color"
                                    name="badge_color"
                                    value={formData.badge_color}
                                    onChange={handleInputChange}
                                    className="w-full h-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                />
                            </div>

                            <div className="flex items-center space-x-4">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="show_countdown"
                                        checked={formData.show_countdown}
                                        onChange={handleInputChange}
                                        className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                                    />
                                    <label className="ml-2 block text-sm text-gray-700">
                                        Geri sayım göster
                                    </label>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="highlight_products"
                                        checked={formData.highlight_products}
                                        onChange={handleInputChange}
                                        className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                                    />
                                    <label className="ml-2 block text-sm text-gray-700">
                                        Ürünleri öne çıkar
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Kupon Ayarları */}
                    {formData.campaign_type === 'coupon' && (
                        <div className="border-t pt-4">
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                                <TagIcon className="w-5 h-5 inline mr-1" />
                                Kupon Ayarları
                            </h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Kupon Kodu *
                                    </label>
                                    <div className="flex">
                                        <input
                                            type="text"
                                            name="coupon_code"
                                            value={formData.coupon_code}
                                            onChange={handleInputChange}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                            placeholder="MINELA2024"
                                        />
                                        <button
                                            type="button"
                                            onClick={generateCouponCode}
                                            className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200 text-sm"
                                        >
                                            Oluştur
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="auto_apply"
                                        checked={formData.auto_apply}
                                        onChange={handleInputChange}
                                        className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                                    />
                                    <label className="ml-2 block text-sm text-gray-700">
                                        Otomatik uygula (kod girmeden)
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Kategori Seçimi */}
                    {(formData.campaign_type === 'category_discount' || formData.campaign_type === 'flash_sale') && (
                        <div className="border-t pt-4">
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                                <TagIcon className="w-5 h-5 inline mr-1" />
                                Kampanya Kategorileri
                            </h4>
                            
                            <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md p-3">
                                <div className="grid grid-cols-1 gap-2">
                                    {categories.map((category) => (
                                        <div key={category.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="checkbox"
                                                checked={formData.selectedCategories.includes(category.id)}
                                                onChange={() => handleCategorySelection(category.id)}
                                                className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                                            />
                                            <div className="flex-1">
                                                <div className="font-medium text-sm">{category.name}</div>
                                                <div className="text-xs text-gray-500">
                                                    {category.slug} {category.parent_id ? '(Alt kategori)' : '(Ana kategori)'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                {categories.length === 0 && (
                                    <div className="text-center py-4 text-gray-500">
                                        Kategoriler yükleniyor...
                                    </div>
                                )}
                            </div>
                            
                            <div className="mt-2 text-sm text-gray-600">
                                {formData.selectedCategories.length} kategori seçildi
                            </div>
                        </div>
                    )}

                    {/* Paket Kampanyası Ayarları */}
                    {formData.campaign_type === 'bundle' && (
                        <div className="border-t pt-4">
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                                <TagIcon className="w-5 h-5 inline mr-1" />
                                Paket Kampanyası Ayarları
                            </h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Alınacak Ürün Sayısı */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Alınacak Ürün Sayısı
                                    </label>
                                    <select
                                        name="package_buy_count"
                                        value={formData.package_buy_count || '2'}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                                    >
                                        <option value="2">2 ürün</option>
                                        <option value="3">3 ürün</option>
                                        <option value="4">4 ürün</option>
                                        <option value="5">5 ürün</option>
                                    </select>
                                </div>

                                {/* Ödenecek Ürün Sayısı */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Ödenecek Ürün Sayısı
                                    </label>
                                    <select
                                        name="package_pay_count"
                                        value={formData.package_pay_count || '1'}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                                    >
                                        <option value="1">1 ürün öde</option>
                                        <option value="2">2 ürün öde</option>
                                        <option value="3">3 ürün öde</option>
                                        <option value="4">4 ürün öde</option>
                                    </select>
                                </div>
                            </div>

                            {/* Paket Türü Seçimi */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Paket Türü
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="package_type"
                                            value="same_product"
                                            checked={formData.package_type === 'same_product'}
                                            onChange={handleInputChange}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">Aynı üründen (örn: 2 adet aynı t-shirt al, 1 tanesini öde)</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="package_type"
                                            value="different_products"
                                            checked={formData.package_type === 'different_products'}
                                            onChange={handleInputChange}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">Farklı ürünlerden (örn: Seçilen ürünlerden 2 al, 1 öde)</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="package_type"
                                            value="category_based"
                                            checked={formData.package_type === 'category_based'}
                                            onChange={handleInputChange}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">Kategori bazlı (örn: T-shirt kategorisinden 2 al, 1 öde)</span>
                                    </label>
                                </div>
                            </div>

                            {/* Önizleme */}
                            <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-sm text-blue-800 font-medium">
                                    📦 Kampanya Önizleme: 
                                    <span className="ml-1">
                                        {formData.package_buy_count || '2'} Al, {formData.package_pay_count || '1'} Öde
                                        {formData.package_type === 'same_product' && ' (Aynı üründen)'}
                                        {formData.package_type === 'different_products' && ' (Farklı ürünlerden)'}
                                        {formData.package_type === 'category_based' && ' (Kategori bazlı)'}
                                    </span>
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Özel Gün Kampanyası Ayarları */}
                    {formData.campaign_type === 'special_day' && (
                        <div className="border-t pt-4">
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                                <TagIcon className="w-5 h-5 inline mr-1" />
                                Özel Gün Kampanyası Ayarları
                            </h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Özel Gün Türü */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Özel Gün Türü
                                    </label>
                                    <select
                                        name="special_day_type"
                                        value={formData.special_day_type || 'mothers_day'}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                                    >
                                        <option value="mothers_day">Anneler Günü (2. Pazar Mayıs)</option>
                                        <option value="fathers_day">Babalar Günü (3. Pazar Haziran)</option>
                                        <option value="childrens_day">23 Nisan Çocuklar Günü</option>
                                        <option value="new_year">Yeni Yıl</option>
                                        <option value="valentines_day">Sevgililer Günü (14 Şubat)</option>
                                        <option value="back_to_school">Okula Dönüş (1-15 Eylül)</option>
                                        <option value="semester_break">Sömestr Tatili (Ocak-Şubat)</option>
                                        <option value="summer_vacation">Yaz Tatili (Haziran-Ağustos)</option>
                                        <option value="report_card_day">Karne Günü</option>
                                        <option value="custom_date">Özel Tarih</option>
                                    </select>
                                </div>

                                {/* Özel Tarih (Custom Date seçildiğinde) */}
                                {formData.special_day_type === 'custom_date' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Özel Tarih
                                        </label>
                                        <input
                                            type="date"
                                            name="special_day_date"
                                            value={formData.special_day_date}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Hedef Kitle */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Hedef Kitle
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="target_audience"
                                            value="all"
                                            checked={formData.target_audience === 'all'}
                                            onChange={handleInputChange}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">Tüm müşteriler</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="target_audience"
                                            value="new_customers"
                                            checked={formData.target_audience === 'new_customers'}
                                            onChange={handleInputChange}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">Yeni müşteriler (ilk alışveriş)</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="target_audience"
                                            value="returning_customers"
                                            checked={formData.target_audience === 'returning_customers'}
                                            onChange={handleInputChange}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">Sadık müşteriler (2+ alışveriş)</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="target_audience"
                                            value="vip_customers"
                                            checked={formData.target_audience === 'vip_customers'}
                                            onChange={handleInputChange}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">VIP müşteriler (5+ alışveriş)</span>
                                    </label>
                                </div>
                            </div>

                            {/* Otomatik Aktivasyon */}
                            <div className="mb-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="auto_activate"
                                        checked={formData.auto_activate}
                                        onChange={handleInputChange}
                                        className="mr-2"
                                    />
                                    <span className="text-sm font-medium">Özel günde otomatik olarak aktif hale getir</span>
                                </label>
                                <p className="text-xs text-gray-500 mt-1">
                                    Bu seçenek işaretliyse, kampanya belirlenen özel günde otomatik olarak aktif olur
                                </p>
                            </div>

                            {/* Kampanya Süresi */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Kampanya Başlangıç Saati
                                    </label>
                                    <input
                                        type="time"
                                        name="campaign_start_time"
                                        value={formData.campaign_start_time || '00:00'}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Kampanya Bitiş Saati
                                    </label>
                                    <input
                                        type="time"
                                        name="campaign_end_time"
                                        value={formData.campaign_end_time || '23:59'}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                                    />
                                </div>
                            </div>

                            {/* Önizleme */}
                            <div className="bg-purple-50 p-3 rounded-lg">
                                <p className="text-sm text-purple-800 font-medium">
                                    🎉 Kampanya Önizleme: 
                                    <span className="ml-1">
                                        {(() => {
                                            const dayNames = {
                                                'mothers_day': 'Anneler Günü',
                                                'fathers_day': 'Babalar Günü', 
                                                'childrens_day': '23 Nisan Çocuklar Günü',
                                                'new_year': 'Yeni Yıl',
                                                'valentines_day': 'Sevgililer Günü',
                                                'back_to_school': 'Okula Dönüş',
                                                'semester_break': 'Sömestr Tatili',
                                                'summer_vacation': 'Yaz Tatili',
                                                'report_card_day': 'Karne Günü',
                                                'custom_date': 'Özel Tarih'
                                            };
                                            const audienceNames = {
                                                'all': 'Tüm müşteriler',
                                                'new_customers': 'Yeni müşteriler',
                                                'returning_customers': 'Sadık müşteriler',
                                                'vip_customers': 'VIP müşteriler'
                                            };
                                            return `${dayNames[formData.special_day_type]} - ${audienceNames[formData.target_audience]}`;
                                        })()}
                                        {formData.auto_activate && ' (Otomatik aktif)'}
                                    </span>
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Ürün Seçimi */}
                    {(formData.campaign_type === 'flash_sale' || (formData.campaign_type === 'bundle' && formData.package_type === 'different_products')) && (
                        <div className="border-t pt-4">
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                                <TagIcon className="w-5 h-5 inline mr-1" />
                                Kampanya Ürünleri
                            </h4>
                        
                        <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md p-3">
                            <div className="grid grid-cols-1 gap-2">
                                {products.map((product) => (
                                    <div key={product.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="checkbox"
                                            checked={formData.selectedProducts.includes(product.id)}
                                            onChange={() => handleProductSelection(product.id)}
                                            className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                                        />
                                        <img
                                            src={product.main_image_url || '/placeholder.jpg'}
                                            alt={product.name}
                                            className="w-12 h-12 object-cover rounded"
                                        />
                                        <div className="flex-1">
                                            <div className="font-medium text-sm">{product.name}</div>
                                            <div className="text-xs text-gray-500">{product.price} TL</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {products.length === 0 && (
                                <div className="text-center py-4 text-gray-500">
                                    Henüz ürün bulunmuyor
                                </div>
                            )}
                        </div>
                        
                            <div className="mt-2 text-sm text-gray-600">
                                {formData.selectedProducts.length} ürün seçildi
                            </div>
                        </div>
                    )}

                    {/* Butonlar */}
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-brand-green text-white rounded-md hover:bg-brand-green-light disabled:opacity-50"
                        >
                            {loading ? 'Kaydediliyor...' : (editingCampaign ? 'Güncelle' : 'Oluştur')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CampaignCreateModal;